import { REPORT_DETAILS_MAX, REPORT_REASON_LABEL, REPORT_REASONS, type ReportReason } from '@calling/shared';
import { useId, useState, type FormEvent } from 'react';
import { RpcError } from '../call/signaling';
import { ApiError, NetworkError } from '../lib/api';
import { useServerInfo } from '../lib/serverInfo';
import '../styles/moderation.css';
import { Dialog } from './ui';

/** What is being reported, for the dialog's title and its small print. */
export type ReportSubject =
  | { kind: 'person'; name: string }
  | { kind: 'message'; name: string; text: string }
  /** `ownerName`: unknown on the page a link opens, which names nobody. */
  | { kind: 'recording'; ownerName?: string };

export interface ReportInput {
  reason: ReportReason;
  details: string;
  /** A guest's address, to hear what was done. Only asked of someone not signed in. */
  contact?: string;
  /** "Also hide them for me", in a meeting. */
  hide: boolean;
}

const EMERGENCY = 'If someone is in danger right now, call your local emergency number.';

/** The words for a report that could not be sent, whatever refused it. */
function problem(err: unknown, write: string | null): string {
  if (err instanceof NetworkError || (err instanceof RpcError && (err.code === 'disconnected' || err.code === 'timeout'))) {
    return 'Your report wasn’t sent. Check your connection and try again.';
  }
  // An older server, which does not know reports yet.
  if (err instanceof RpcError && err.code === 'invalid' && err.message === 'Unknown request.') {
    return `Reporting isn’t available right now.${write ? ` Write to ${write}.` : ''}`;
  }
  if ((err instanceof RpcError || err instanceof ApiError) && err.message) return err.message;
  return 'Your report wasn’t sent. Try again.';
}

/**
 * Reports a person, a message or a recording to the people who run the
 * service. The person reported isn't told who reported them; nobody in the
 * meeting is told anything. `send` does the sending and throws what refused
 * it; `onSent` closes the dialog and says thank you (a report about a child
 * at risk shows the emergency sentence again first).
 */
export function ReportDialog({
  subject,
  canHide = false,
  askContact = false,
  send,
  onSent,
  onClose,
}: {
  subject: ReportSubject;
  canHide?: boolean;
  askContact?: boolean;
  send: (input: ReportInput) => Promise<void>;
  onSent: (input: ReportInput) => void;
  onClose: () => void;
}) {
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [details, setDetails] = useState('');
  const [contact, setContact] = useState('');
  const [hide, setHide] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [urgentSent, setUrgentSent] = useState<ReportInput | null>(null);
  const info = useServerInfo().info;
  const write = info?.legal?.contact.reports ?? info?.supportEmail ?? null;
  const ids = useId();

  const who = subject.kind === 'recording' ? (subject.ownerName ?? null) : subject.name;
  const notTold = who ? `${who.split(' ')[0] || who} won’t be told who reported ${subject.kind === 'recording' ? 'it' : 'them'}.` : 'The person who shared it won’t be told who reported it.';
  const title = subject.kind === 'person' ? `Report ${who}` : subject.kind === 'message' ? 'Report this message' : 'Report this recording';
  const quoted = subject.kind === 'message' ? (Array.from(subject.text).length > 200 ? `${Array.from(subject.text).slice(0, 200).join('')}…` : subject.text) : null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!reason || busy) return;
    const trimmed = contact.trim();
    if (askContact && trimmed && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return setError('Enter an email address like name@example.com, or leave it empty.');
    const input: ReportInput = { reason, details: details.trim(), hide: canHide && hide, ...(askContact && trimmed ? { contact: trimmed } : {}) };
    setBusy(true);
    setError('');
    try {
      await send(input);
      if (reason === 'child_safety') setUrgentSent(input);
      else onSent(input);
    } catch (err) {
      setError(problem(err, write));
    } finally {
      setBusy(false);
    }
  };

  if (urgentSent) {
    return (
      <Dialog title="Report sent" onClose={() => onSent(urgentSent)} testId="report-urgent">
        <p>Thank you. We look at reports about a child at risk first.</p>
        <p>
          <b>If a child may be in danger right now, call your local emergency number.</b>
        </p>
        <div className="dialog__actions">
          <button className="btn btn--primary" onClick={() => onSent(urgentSent)}>
            OK
          </button>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog title={title} onClose={onClose} testId="report-dialog">
      <form className="stack" onSubmit={submit} noValidate>
        {quoted ? (
          <blockquote className="report-quote" data-testid="report-quote">
            <b>{subject.kind === 'message' ? subject.name : ''}</b>
            <span>{quoted}</span>
          </blockquote>
        ) : null}
        <fieldset className="choices">
          <legend className="label">What’s wrong?</legend>
          {REPORT_REASONS.map((r) => (
            <label key={r} className="choice">
              <input type="radio" name={`${ids}-reason`} value={r} checked={reason === r} onChange={() => setReason(r)} />
              <span>{REPORT_REASON_LABEL[r]}</span>
            </label>
          ))}
        </fieldset>
        <div className="field">
          <label htmlFor={`${ids}-details`}>Anything else we should know? (optional)</label>
          <textarea
            id={`${ids}-details`}
            className="input"
            rows={3}
            maxLength={REPORT_DETAILS_MAX}
            value={details}
            onChange={(e) => setDetails(e.target.value)}
          />
        </div>
        {askContact ? (
          <div className="field">
            <label htmlFor={`${ids}-contact`}>Your email (optional)</label>
            <input id={`${ids}-contact`} className="input" type="email" autoComplete="email" inputMode="email" value={contact} onChange={(e) => setContact(e.target.value)} />
            <span className="hint">Only if you’d like to hear what we did.</span>
          </div>
        ) : null}
        {canHide ? (
          <label className="check-row">
            <input type="checkbox" checked={hide} onChange={(e) => setHide(e.target.checked)} />
            <span>Also hide them for me</span>
          </label>
        ) : null}
        <p className="note" style={{ margin: 0 }}>
          {notTold} We look at every report within 24 hours. {EMERGENCY}
        </p>
        {error ? (
          <p className="report-error" role="alert" data-testid="report-error">
            {error}
          </p>
        ) : null}
        <div className="dialog__actions">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn--primary" disabled={!reason || busy}>
            {busy ? 'Sending…' : 'Send report'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
